<template>
  <div class="send-sms-form">
    <div class="field-wrapper">
      <BaseInformation message="automatedScenarios.informations.sendSms" />
    </div>

    <Loader v-if="isLoading" />

    <!-- Template Selector -->
    <TemplateSelectorV2
      v-else-if="templateSelectorIsOpen"
      type-templates="sms"
      :origin-call="campaignType"
      :id-campaign="campaignId"
      :duplicate="true"
      :current-template-name="templateConfig.activePreviewTemplate.value.label"
      @on-choose-template="handleSelectTemplate"
      @on-close-dialog="handleCloseTemplateSelector"
    />

    <!-- Main Configuration -->
    <PanelAccordion
      v-else
      :key="panelsKey"
      :always-keep-one-open="false"
      :panels="panels"
    >
      <!-- Template Preview -->
      <template #template_preview>
        <TemplatePreview
          :templates="templateConfig.listTemplates.value"
          :active-media="templateConfig.activePreviewMedia.value"
          :active-template-index="templateConfig.activePreviewTemplateIndex.value"
          :image-load-time="templateConfig.imageLoadTime.value"
          :image-refresh-key="templateConfig.tmpImgRefreshKey.value"
          :show-send-test="true"
          @tab-change="templateConfig.handleActivePreviewTemplateChange"
          @select-media="templateConfig.selectPreviewMedia"
          @change-template="handleChangeTemplate"
          @edit-template="handleEditTemplate"
          @send-test="openSendTestWithValidation"
          @image-loaded="templateConfig.handleImageLoaded"
          @image-failed="templateConfig.handleImageFailed"
        />
      </template>

      <!-- Voucher Configuration (Multi-voucher or legacy) -->
      <template #voucher_config>
        <!-- Multi-voucher system: reuse selector is inside each template's modal -->
        <MultiVoucherConfig
          v-if="useMultiVoucherConfig && dataFields.template_vouchers"
          v-model="dataFields.template_vouchers"
          :templates="templateConfig.listTemplates.value"
          :form-validation="formValidationErrors"
          :allow-automatic-method="templateConfig.allowAutomaticVoucherMethod.value"
          :show-reuse-previous-coupon-field="showReusePreviousCouponField"
          :reuse-previous-coupon-options="reusePreviousCouponOptions"
          :resolved-parent-voucher="resolvedParentVoucher"
          :resolved-expiring-voucher="resolvedExpiringVoucher"
        />
        <!-- Legacy VoucherConfig with global reuse selector -->
        <template v-else>
          <ReuseCouponSelector
            v-if="showReusePreviousCouponField"
            v-model="dataFields.reuse_a_previous_coupon"
            :options="reusePreviousCouponOptions"
          />
          <VoucherConfig
            v-if="showVoucherConfig"
            v-model="dataFields"
            :form-validation="formValidationErrors"
            :allow-automatic-method="templateConfig.allowAutomaticVoucherMethod.value"
          />
        </template>
      </template>

      <!-- Message Configuration (Sender Name) -->
      <template #message_config>
        <MessageConfig
          v-model="messageData"
          media="sms"
          :active-template-id="templateConfig.activePreviewTemplate.value.id_template"
          :form-validation="formValidationErrors"
        />
      </template>

      <!-- UTM Configuration -->
      <template #utm_config>
        <UtmConfig v-model="utmData" />
      </template>
    </PanelAccordion>

    <!-- Send Test Modal -->
    <SendTestModal
      v-if="showSendTestModal"
      :type-of-input="templateConfig.activePreviewTemplate.value.type"
      @on-validate="handleSendTest"
      @on-close-dialog="handleCloseSendTestModal"
    />
    <SendTestV4Modal
      v-if="!showSendTestModal"
      channel="sms"
      @on-validate="handleSendTestV4"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  reactive,
  computed,
  watch,
  toRef,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Loader from '@/components/layout/Loader.vue';
import BaseInformation from '@/components/fields/BaseInformation.vue';
import { PanelAccordion } from '@/components/flows/operators/shared';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import { TemplatePreview, VoucherConfig, MultiVoucherConfig, MessageConfig, ReuseCouponSelector, UtmConfig } from '@/components/flows/operators/shared';
import SendTestModal from '@/components/modals/SendTestModal.vue';
import SendTestV4Modal from '@/components/modals/SendTestV4Modal.vue';
import { useSendingChannelForm } from '@/composables/flows';
import { useUtmData } from '@/components/flows/operators/shared/utmDefaults';
import { retrieveServicesData } from '@/composables/shop/MyShopParameters';
import { showToastError } from '@/helpers';
import type { SendSmsData } from './index';
import { createDefaultData } from './index';
import SendSms from './index';

export default defineComponent({
  name: 'ActionSendSmsForm',

  components: {
    BaseInformation,
    Loader,
    PanelAccordion,
    TemplateSelectorV2,
    TemplatePreview,
    VoucherConfig,
    MultiVoucherConfig,
    MessageConfig,
    ReuseCouponSelector,
    UtmConfig,
    SendTestModal,
    SendTestV4Modal,
  },

  props: {
    modelValue: {
      type: Object as PropType<SendSmsData>,
      required: true,
      default: () => createDefaultData(),
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    selectedComponent: {
      type: String,
      default: '',
    },

    operatorId: {
      type: String,
      default: undefined,
    },
  },

  emits: ['update:modelValue', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const panelsKey = ref(0);

    // Reactive data fields
    const dataFields = reactive<SendSmsData>(createDefaultData({ ...props.modelValue }));

    // Initialize sending channel form composable
    const sendingChannelForm = useSendingChannelForm({
      media: 'sms',
      dataFields,
      formValidation: toRef(props, 'formValidation'),
      panelsKey,
      operatorId: props.operatorId,
      onCancel: () => emit('cancel'),
    });

    // Destructure commonly used values from composable
    const {
      campaignId,
      campaignType,
      shopId,
      templateConfig,
      templateSelectorIsOpen,
      formValidationErrors,
      voucherData,
      messageData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      handleSendTest,
      handleSendTestV4,
      handleOpenSendTestModal,
      handleCloseSendTestModal,
      showSendTestModal,
      initialize,
    } = sendingChannelForm;

    // Wrapper to open send test modal with validation
    const openSendTestWithValidation = () => {
      handleOpenSendTestModal(() => SendSms.validate!(dataFields, { shopId: shopId.value }));
    };

    // UTM data computed
    const utmData = useUtmData(dataFields);

    // Panels configuration (specific to SMS)
    const panels = computed(() => [
      {
        id: 'template_preview',
        title: 'automatedScenarios.actions.sending_channel.template_preview.title',
      },
      {
        id: 'voucher_config',
        title: 'automatedScenarios.actions.sending_channel.vouchers_config.title',
        hide: !showVoucherPanel.value,
      },
      {
        id: 'message_config',
        title: 'automatedScenarios.actions.sending_channel.message_config.title',
      },
      {
        id: 'utm_config',
        title: 'automatedScenarios.actions.sending_channel.utm_config.title',
      },
    ]);

    // Emit updates when data changes
    watch(dataFields, () => {
      emit('update:modelValue', createDefaultData({ ...dataFields }));
    }, { deep: true });

    // Initialize on mount
    onMounted(async () => {
      try {
        isLoading.value = true;

        // Load default sender name if not set
        if (!Object.values(dataFields.template_message_expname).some((v) => v !== null && v !== '')) {
          const field = reactive<Record<string, any>>({
            shopRequiredDataSms_senderName: {},
          });
          await retrieveServicesData(shopId.value, field);
          dataFields.template_message_expname = field.shopRequiredDataSms_senderName;
        }

        // Initialize template config
        await initialize(dataFields.template_display_selector_id);
      } catch (error) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      t,
      isLoading,
      panelsKey,
      formValidationErrors,
      templateConfig,
      templateSelectorIsOpen,
      dataFields,
      voucherData,
      messageData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      panels,
      campaignId,
      campaignType,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      handleSendTest,
      handleSendTestV4,
      handleOpenSendTestModal,
      handleCloseSendTestModal,
      showSendTestModal,
      openSendTestWithValidation,
      utmData,
    };
  },
});
</script>
